// src/components/auth/signin/Singin.jsx

import styled from "@emotion/styled";
import {
  Button,
  Stack,
  Typography,
  Box,
  Divider,
  Paper,
  IconButton,
} from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import Cookies from "universal-cookie";
import { postApiServices } from "../../../apis/api";
import { apiRoutes } from "../../../apis/apiPath";
import { singinInitialValues } from "../../../constants";
import { setUserData } from "../../../redux/MainReducer";
import { store } from "../../../redux/Store";
import { ROUTE_PATHS } from "../../../routes/routePath";
import { errorToast, successToast, sanitizeUserError } from "../../../utils/helper";
import PublicIcon from "@mui/icons-material/Public";
import PersonAddAltIcon from "@mui/icons-material/PersonAddAlt";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import LockResetIcon from "@mui/icons-material/LockReset";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

// Theme color
const PRIMARY_COLOR = "#7285b3";
const PRIMARY_DARK = "#5a6a94";

const PageContainer = styled("div")({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  minHeight: "100vh",
  backgroundColor: "#f5f6fa",
  padding: "20px",
});

const LoginCard = styled(Paper)({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  padding: "40px",
  borderRadius: "16px",
  maxWidth: "450px",
  width: "100%",
  boxShadow: "0 10px 40px rgba(0, 0, 0, 0.1)",
});

const LogoContainer = styled(Box)({
  display: "flex",
  alignItems: "center",
  marginBottom: "24px",
});

const StyledInput = styled("input")(({ hasError }) => ({
  padding: "14px 20px",
  width: "100%",
  borderRadius: "100px",
  fontSize: "16px",
  marginBottom: "16px",
  borderWidth: 1,
  borderStyle: "solid",
  borderColor: hasError ? "#d32f2f" : "#e0e0e0",
  fontFamily: "Poppins, sans-serif",
  backgroundColor: "#F5F5F5",
  boxSizing: "border-box",
  transition: "all 0.3s ease",
  "&:focus": {
    outline: "none",
    borderColor: PRIMARY_COLOR,
    boxShadow: `0 0 0 3px rgba(114, 133, 179, 0.2)`,
  },
  "&:disabled": {
    backgroundColor: "#eee",
    cursor: "not-allowed",
  },
}));

const StyledButton = styled(Button)({
  padding: "14px 20px",
  width: "100%",
  borderRadius: "100px",
  color: "white",
  fontSize: "16px",
  fontWeight: 600,
  marginTop: "8px",
  backgroundColor: PRIMARY_COLOR,
  fontFamily: "Poppins, sans-serif",
  textTransform: "none",
  transition: "all 0.3s ease",
  "&:hover": {
    backgroundColor: PRIMARY_DARK,
    transform: "translateY(-2px)",
    boxShadow: "0 4px 12px rgba(114, 133, 179, 0.4)",
  },
  "&:disabled": {
    backgroundColor: "#ccc",
  },
});

const SecondaryButton = styled(Button)({
  padding: "12px 20px",
  width: "100%",
  borderRadius: "100px",
  fontSize: "14px",
  fontWeight: 500,
  fontFamily: "Poppins, sans-serif",
  textTransform: "none",
  transition: "all 0.3s ease",
  borderColor: "#e0e0e0",
  color: "#666",
  "&:hover": {
    borderColor: PRIMARY_COLOR,
    color: PRIMARY_COLOR,
    backgroundColor: "rgba(114, 133, 179, 0.05)",
  },
});

const LoginText = styled(Typography)({
  fontSize: "28px",
  fontWeight: "bold",
  marginBottom: "8px",
  fontFamily: "Poppins, sans-serif",
  color: "#333",
});

const SubText = styled(Typography)({
  fontSize: "14px",
  marginBottom: "24px",
  fontFamily: "Poppins, sans-serif",
  color: "#666",
});

const MessageBox = styled("div")(({ type }) => ({
  width: "100%",
  padding: "12px 16px",
  borderRadius: "8px",
  marginBottom: "16px",
  fontSize: "14px",
  fontFamily: "Poppins, sans-serif",
  textAlign: "center",
  backgroundColor:
    type === "error" ? "#ffebee" : type === "info" ? "#e3f2fd" : "#e8f5e9",
  color: type === "error" ? "#c62828" : type === "info" ? "#1565c0" : "#2e7d32",
  border: `1px solid ${
    type === "error" ? "#ef9a9a" : type === "info" ? "#90caf9" : "#a5d6a7"
  }`,
}));

const StyledDivider = styled(Divider)({
  width: "100%",
  margin: "24px 0",
  "&::before, &::after": {
    borderColor: "#e0e0e0",
  },
});

const LinkText = styled(Typography)({
  fontSize: "14px",
  color: "#666",
  fontFamily: "Poppins, sans-serif",
  "& a": {
    color: PRIMARY_COLOR,
    textDecoration: "none",
    fontWeight: 500,
    "&:hover": {
      textDecoration: "underline",
    },
  },
});

export const Singin = () => {
  const cookies = new Cookies();
  const navigate = useNavigate();
  const [values, setValues] = useState(singinInitialValues);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [showPassword, setShowPassword] = useState(false);
  const valuesRef = React.useRef(values);

  useEffect(() => {
    valuesRef.current = values;
  }, [values]);

  useEffect(() => {
    // Check if Google GSI is loaded
    const initializeGoogle = () => {
      if (window.google && window.google.accounts) {
        window.google.accounts.id.initialize({
          client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
          callback: handleGoogleResponse,
        });

        window.google.accounts.id.renderButton(
          document.getElementById("google-signin-button"),
          { 
            theme: "outline", 
            size: "large", 
            width: "380", 
            shape: "pill",
            logo_alignment: "center"
          }
        );
      }
    };

    // If already loaded, initialize
    initializeGoogle();

    // If not loaded, wait for it
    window.addEventListener("load", initializeGoogle);
    return () => window.removeEventListener("load", initializeGoogle);
  }, []);

  const handleGoogleResponse = (response) => {
    const currentValues = valuesRef.current;
    if (!currentValues.tenantName) {
      setMessage({ type: "error", text: "Please enter a Company Name first to use Google Sign-in" });
      return;
    }
    
    // Generate or get session ID (mandatory for this REST API)
    let sessionId = sessionStorage.getItem("pd_session_id");
    if (!sessionId) {
      sessionId = crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).substring(2);
      sessionStorage.setItem("pd_session_id", sessionId);
    }

    googleLogin.mutate({
      credential: response.credential,
      sessionID: sessionId,
      tenantName: currentValues.tenantName,
    });
  };

  const googleLogin = useMutation({
    mutationFn: (payload) => postApiServices(apiRoutes.googleLogin, payload),
    onSuccess: async ({ data }) => {
      if (data?.success && data?.token) {
        handleLoginSuccess(data);
      } else {
        errorToast(sanitizeUserError(data?.message, "Google sign-in failed. Please try again."));
        setMessage({
          type: "error",
          text: sanitizeUserError(data?.message, "Google sign-in failed. Please try again."),
        });
      }
    },
    onError: (error) => {
      const msg = sanitizeUserError(
        error?.response?.data?.message,
        "We couldn't sign you in with Google right now. Please try again in a few minutes."
      );
      errorToast(msg);
      setMessage({ type: "error", text: msg });
    },
  });

  useEffect(() => {
    const savedTenant = cookies.get("tenantName");
    if (savedTenant) {
      setValues((prev) => ({
        ...prev,
        tenantName: savedTenant,
      }));
    }
  }, []);

  const handleLoginSuccess = (data) => {
    cookies.set("token", data.token, { path: "/" });
    cookies.set("userId", data.userId, { path: "/" });
    cookies.set("userFullName", data.userFullName, { path: "/" });
    cookies.set("defaultcompany", data.defaultcompany, { path: "/" });
    cookies.set("tenantId", data.tenantId, { path: "/" });
    cookies.set("userEmail", data.userEmail, { path: "/" });
    cookies.set("isTenantAdmin", data.isTenantAdmin, { path: "/" });
    cookies.set("isSystemAdmin", data.isSystemAdmin, { path: "/" });
    cookies.set("activeSubscription", data.activeSubscription, {
      path: "/",
    });
    cookies.set("tenantEnabled", data.tenantEnabled ?? true, { path: "/" });

    const currentTenantName = values.tenantName || cookies.get("tenantName");
    if (currentTenantName) {
      cookies.set("tenantName", currentTenantName, { path: "/", maxAge: 31536000 });
    }

    sessionStorage.setItem("justLoggedIn", "true");
    store.dispatch(setUserData({ ...data }));
    successToast("Login successful!");
    if (data.defaulttenant) {
      navigate(ROUTE_PATHS.COMPANY_LIST);
    } else {
      navigate(ROUTE_PATHS.HOME);
    }
  };

  const onChange = (e) => {
    setValues((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
    if (message.text) {
      setMessage({ type: "", text: "" });
    }
  };

  const login = useMutation({
    mutationFn: () => postApiServices(apiRoutes.login, values),
    onSuccess: async ({ data }) => {
      if (!data?.success) {
        switch (data?.error) {
          case "PENDING_APPROVAL":
            setMessage({ type: "info", text: "⏳ " + data.message });
            break;
          case "INACTIVE_ACCOUNT":
            setMessage({ type: "error", text: "🚫 " + data.message });
            break;
          case "WRONG_PASSWORD":
            setMessage({ type: "error", text: "🔐 " + data.message });
            break;
          case "USER_NOT_FOUND":
            setMessage({ type: "error", text: "👤 " + data.message });
            break;
          case "UNKNOWN_TENANT":
            setMessage({ type: "error", text: "🏢 " + data.message });
            break;
          case "IP_NOT_ALLOWED":
            setMessage({ type: "error", text: "🌐 " + data.message });
            break;
          default:
            setMessage({
              type: "error",
              text: sanitizeUserError(
                data?.message,
                "We couldn't sign you in right now. Please try again in a few minutes. If this continues, contact support."
              ),
            });
            errorToast(
              sanitizeUserError(
                data?.message,
                "We couldn't sign you in right now. Please try again in a few minutes. If this continues, contact support."
              )
            );
        }
        return;
      }

      if (data?.requires2FA) {
        setMessage({ type: "info", text: "📧 " + data.message });
        successToast(data.message);
        return;
      }

      if (data?.token) {
        handleLoginSuccess(data);
      }
    },
    onError: (error) => {
      const raw =
        error?.response?.data?.message ||
        error?.response?.data ||
        error?.message ||
        "";
      const errorMessage = sanitizeUserError(
        raw,
        "We couldn't sign you in right now. Please try again in a few minutes. If this continues, contact support."
      );

      setMessage({ type: "error", text: errorMessage });
      errorToast(errorMessage);
    },
  });

  const handleSubmit = () => {
    setMessage({ type: "", text: "" });

    if (!Object.values(values)?.every((val) => val !== "")) {
      setMessage({ type: "error", text: "Please fill all the fields" });
      return;
    }

    login.mutate();
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSubmit();
    }
  };

  return (
    <PageContainer>
      <LoginCard elevation={0}>
        {/* Logo */}
        <LogoContainer>
          <PublicIcon sx={{ color: PRIMARY_COLOR, fontSize: 50, mr: 1 }} />
          <Typography
            variant="h5"
            sx={{ color: PRIMARY_COLOR, fontWeight: 700, fontFamily: "Poppins" }}
          >
            Premierdis
          </Typography>
        </LogoContainer>

        <SubText>Sign in to continue to your account</SubText>

        {/* Message */}
        {message.text && <MessageBox type={message.type}>{message.text}</MessageBox>}

        {/* Form */}
        <Box sx={{ width: "100%" }}>
          <StyledInput
            name="tenantName"
            type="text"
            placeholder="Company Name"
            value={values?.tenantName}
            onChange={onChange}
            onKeyPress={handleKeyPress}
            disabled={login.isPending}
          />
          <StyledInput
            name="username"
            type="text"
            placeholder="Username"
            value={values?.username}
            onChange={onChange}
            onKeyPress={handleKeyPress}
            disabled={login.isPending}
          />
          <Box sx={{ position: "relative", width: "100%", mb: "16px" }}>
            <StyledInput
              name="password"
              type={showPassword ? "text" : "password"}
              placeholder="Password"
              value={values?.password}
              onChange={onChange}
              onKeyPress={handleKeyPress}
              disabled={login.isPending}
              style={{ paddingRight: "50px", marginBottom: 0 }}
            />
            <IconButton
              onClick={() => setShowPassword(!showPassword)}
              size="small"
              tabIndex={-1}
              sx={{
                position: "absolute",
                right: "16px",
                top: "50%",
                transform: "translateY(-50%)",
                color: "#666",
              }}
            >
              {showPassword ? <VisibilityOff fontSize="medium" /> : <Visibility fontSize="medium" />}
            </IconButton>
          </Box>

          {/* Forgot Password Link */}
          <Box sx={{ textAlign: "right", mb: 2 }}>
            <Link
              to={ROUTE_PATHS.FORGOT_PASSWORD}
              style={{
                color: PRIMARY_COLOR,
                textDecoration: "none",
                fontSize: "14px",
                fontFamily: "Poppins",
              }}
            >
              Forgot Password?
            </Link>
          </Box>

          <StyledButton
            variant="contained"
            onClick={handleSubmit}
            disabled={login.isPending || googleLogin.isPending}
          >
            {login.isPending ? "Signing in..." : "Sign In"}
          </StyledButton>

          <StyledDivider>
            <Typography sx={{ color: "#999", fontSize: "12px", fontFamily: "Poppins" }}>
              OR
            </Typography>
          </StyledDivider>

          <Box 
            sx={{ 
              width: "100%", 
              mt: 2, 
              height: "52px", 
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              position: "relative", // Added for overlay positioning
            }}
          >
            <Box 
              id="google-signin-button" 
              sx={{ 
                transform: "scale(1.18)", // Scale up slightly to match the 52px height
                transformOrigin: "center"
              }} 
            />

            {/* Overlay to intercept clicks if tenantName is empty */}
            {!values.tenantName && (
              <Box 
                onClick={() => {
                  setMessage({ 
                    type: "error", 
                    text: "Please enter a Company Name first to use Google Sign-in" 
                  });
                  // Optionally focus the tenant name input
                  const tenantInput = document.getElementsByName("tenantName")[0];
                  if (tenantInput) tenantInput.focus();
                }}
                sx={{ 
                  position: "absolute", 
                  top: 0, 
                  left: "50%",
                  transform: "translateX(-50%)",
                  width: "100%",
                  maxWidth: "400px", // Match approximate scaled button width
                  height: "100%",
                  zIndex: 10,
                  cursor: "pointer",
                  borderRadius: "100px",
                  backgroundColor: "transparent",
                }} 
              />
            )}
          </Box>
        </Box>

        {/* Divider */}
        {/* <StyledDivider>
          <Typography sx={{ color: "#999", fontSize: "12px", fontFamily: "Poppins" }}>
            OR
          </Typography>
        </StyledDivider> */}

        {/* Secondary Actions */}
        {/* <Stack spacing={2} sx={{ width: "100%" }}> */}
          {/* Sign Up Button */}
          {/* <SecondaryButton
            variant="outlined"
            startIcon={<PersonAddAltIcon />}
            onClick={() => navigate(ROUTE_PATHS.SIGNUP)}
          >
            Create New Account
          </SecondaryButton> */}

          {/* Admin Login Button */}
          {/* <SecondaryButton
            variant="outlined"
            startIcon={<AdminPanelSettingsIcon />}
            onClick={() => navigate(ROUTE_PATHS.ADMIN_LOGIN)}
          >
            Admin Login
          </SecondaryButton> */}
        {/* </Stack> */}
      </LoginCard>
    </PageContainer>
  );
};